// src/ApiPanel/hooks/useReportQuery.js
// One place for the load / loading / error / stale-response dance that every
// report page needs.
//
// The part that is easy to get wrong and matters: responses are sequenced.
// If someone changes the date range while a slower request for the previous
// range is still in flight, the stale response must not overwrite the newer
// one — on a financial report that means showing figures for a period the
// filter bar says you are not looking at. Each run takes a ticket, and only
// the newest ticket is allowed to set state.
import { useCallback, useEffect, useRef, useState } from "react";
import { apiPanelFetch, qs } from "../utils/apiPanelFetch";

export function useReportQuery(endpoint, params, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");

  const ticketRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Serialising the params gives a stable dependency without asking every
  // caller to useMemo their filter object.
  const key = JSON.stringify(params || {});

  const run = useCallback(async () => {
    if (!enabled) return;
    const ticket = ++ticketRef.current;
    setLoading(true);
    setError("");
    try {
      const res = await apiPanelFetch(`${endpoint}${qs(JSON.parse(key))}`);
      if (ticket !== ticketRef.current || !mountedRef.current) return;
      setData(res);
    } catch (err) {
      if (ticket !== ticketRef.current || !mountedRef.current) return;
      setError(err.message || "Could not load this report.");
      setData(null);
    } finally {
      if (ticket === ticketRef.current && mountedRef.current) setLoading(false);
    }
  }, [endpoint, key, enabled]);

  useEffect(() => {
    run();
  }, [run]);

  return { data, loading, error, reload: run };
}

/**
 * POST an action and report on it, without letting a double-click submit
 * twice. `busy` is set before the request starts and only cleared after it
 * settles, and the guard is checked inside the same call — on a financial
 * write (approving a challan, committing a batch) a duplicate submit is not
 * a cosmetic problem.
 */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const inFlight = useRef(false);

  const run = useCallback(async (endpoint, payload, { form = false } = {}) => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await apiPanelFetch(endpoint, {
        method: "POST",
        ...(form ? { form: payload } : { json: payload }),
      });
      setMessage(res?.message || "Done.");
      return res;
    } catch (err) {
      setError(err.message || "That action could not be completed.");
      return null;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError("");
    setMessage("");
  }, []);

  return { run, busy, error, message, reset };
}
